import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { getAccountId } from '~/helpers'
import { capitalizeWords } from '~/helpers/capitalize-words'

import { getPublishersOnsite } from '../services/publisher'

/**
 * Key própria, e **não** `getPublishersOnsiteKey`. O seletor do formulário de
 * criação (`useDestinationRetailer`) já cacheia essa mesma lista naquela key,
 * mas mapeada para opções de campo — com `label` e um `icon` de JSX. Duas
 * queries na mesma key com `queryFn` diferentes devolvem o que rodou primeiro,
 * e aqui isso seria um objeto de forma errada em vez de um erro.
 */
export const destinationPublisherNamesKey = 'destinationPublisherNames'

/**
 * Pedido explícito, em vez de herdar o default do BFF.
 *
 * O restante das páginas sai num `Promise.all`, então o tamanho da página é o
 * que decide quantas requisições simultâneas a tela dispara: com o default de
 * vinte, uma base de quinhentos publishers viraria vinte e cinco chamadas de
 * uma vez só para resolver um ou dois nomes.
 */
const PAGE_SIZE = 100

/**
 * Resolve nomes de varejista de destino a partir dos identificadores que o
 * detalhe da campanha devolve em `destination_publishers`.
 *
 * A campanha só traz ids, e a listagem de publishers onsite é a mesma fonte de
 * onde a criação offsite escolhe o destino. Busca a lista inteira uma vez e
 * resolve em memória: nunca uma requisição por id.
 */
export function useDestinationPublisherNames(publisherIds?: string[]) {
  const ids = useMemo(() => publisherIds?.filter(Boolean) ?? [], [publisherIds])

  const { data, isPending } = useQuery({
    enabled: ids.length > 0,
    /**
     * A conta entra na key porque o `QueryClient` é de módulo, criado uma vez
     * em `app.tsx`, e a troca de conta reinicia só o Redux. Sem isso, dentro dos
     * cinco minutos de `staleTime` a segunda conta reusaria o mapa de nomes da
     * primeira e mostraria varejista de destino de outra conta.
     */
    queryKey: [destinationPublisherNamesKey, getAccountId()],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const firstPage = await getPublishersOnsite({ quantity: PAGE_SIZE })
      const totalPages = firstPage?.data?.pages

      const rest = await Promise.all(
        Array.from({ length: Math.max(0, (totalPages ?? 1) - 1) }, (_, index) =>
          getPublishersOnsite({ page: index + 2, quantity: PAGE_SIZE })
        )
      )

      const all = [
        ...(firstPage?.data?.data ?? []),
        ...rest.flatMap(response => response?.data?.data ?? [])
      ]

      return new Map(
        all.map(item => [
          item.publisher_id as string,
          capitalizeWords(item.publisher_name)
        ])
      )
    }
  })

  /**
   * Id que não resolve é omitido, não substituído por placeholder: a lista traz
   * apenas publishers ativos com `allow_offsite = false`, então um destino
   * desativado depois da criação simplesmente não está lá. Melhor mostrar os
   * que resolveram do que poluir com marcadores de ausência.
   */
  const names = useMemo(
    // `flatMap` em vez de `map().filter(Boolean)` porque o filtro não estreita o
    // tipo: id sem correspondência contribui com nada e o resultado já é
    // `string[]`, sem asserção. O retorno do callback vai anotado para o
    // compilador não ter que unificar `string` com `[]` sozinho.
    () => ids.flatMap((id): string | string[] => data?.get(id) ?? []),
    [data, ids]
  )

  return {
    names,
    isPending: ids.length > 0 && isPending,
    hasDestination: ids.length > 0
  }
}
